import {
  now,
  createTask,
  ScheduleDependOnResult,
  milliseconds
} from "@zyzgroup/core-common";

// requestAnimationFrame 的触发时机是在一帧的开头
let rAF =
  window.requestAnimationFrame ||
  (window as any).webkitRequestAnimationFrame ||
  (window as any).mozRequestAnimationFrame ||
  (window as any).oRequestAnimationFrame ||
  (window as any).msRequestAnimationFrame;

let cAF =
  window.cancelAnimationFrame ||
  (window as any).webkitCancelAnimationFrame ||
  (window as any).mozCancelAnimationFrame ||
  (window as any).oCancelAnimationFrame ||
  (window as any).msCancelAnimationFrame;

// requestAnimationFrame polyfill
if (!rAF) {
  let lastTime = now();
  rAF = (cb: (time: DOMHighResTimeStamp) => void) => {
    const currTime = now();
    const timeRemain = Math.max(0, 16 - (currTime - lastTime)); // 16 = 1000 / 60
    const id = window.setTimeout(() => {
      cb(currTime + timeRemain);
    }, timeRemain);
    lastTime = currTime + timeRemain;
    return id;
  };
}
// cancelAnimationFrame polyfill
if (!cAF) {
  cAF = (id) => {
    window.clearTimeout(id);
  };
}

export { rAF, cAF };

// 自调用 requestAnimationFrame
// cb return false : 结束持续调用
export function requestAnimationFrameCycle(
  cb: (time: DOMHighResTimeStamp) => boolean
): () => void {
  let isCancel = false;
  let id = rAF(function frame(time: DOMHighResTimeStamp) {
    if (!isCancel && cb(time)) {
      id = rAF(frame);
    }
  });
  return () => {
    cAF(id);
    isCancel = true;
  };
}

// return cancelFn
export function setTimeoutAccurate(
  cb: (time: DOMHighResTimeStamp) => void,
  timeout = 0
): () => void {
  if (timeout == 0) {
    // 同步立即执行
    cb(now());
    return () => {};
  } else {
    // 异步执行
    const startTime = now();
    return requestAnimationFrameCycle((time: DOMHighResTimeStamp) => {
      if (time - startTime >= timeout) {
        cb(time);
        return false;
      }
      return true;
    });
  }
}

export function setIntervalAccurate(
  cb: (
    time: DOMHighResTimeStamp,
    count: number,
    isTimeOutInThisCycle: boolean
  ) => void,
  interval = 0
): () => void {
  let isCancel = false;
  let count = 0;
  const startTime = now();
  let cancel = setTimeoutAccurate(function frame(time: DOMHighResTimeStamp) {
    if (isCancel) {
      return;
    }
    count++;
    // 时间偏差
    const offset = time - startTime - count * interval;
    cb(time, count, interval <= offset);
    // 根据时间偏差，计算下次倒计时设定的回调时间，从而达到纠正的目的
    cancel = setTimeoutAccurate(frame, Math.max(0, interval - offset));
  }, interval);
  return () => {
    cancel();
    isCancel = true;
  };
}

// 快于指定帧率调用时，返回缓存值
export function createRunCycleInMaxRate(task: () => any, fps = 20): () => any {
  const duration = 1000 / fps;
  let lastTime = 0;
  let lastResult: any;
  return () => {
    if (now() - lastTime >= duration) {
      lastTime = now();
      lastResult = task();
    }
    return lastResult;
  };
}

/// FPS（frame per second）是浏览器每秒刷新的次数
/// fps 低于 60 人眼就会感知卡顿掉帧
/// 在两次屏幕硬件刷新之间，浏览器正好进行一次刷新（重绘），网页会很流畅
/// 两次硬件刷新之间浏览器重绘多次是没意义的，只会消耗资源
/// 如果浏览器重绘一次的时间是硬件多次刷新的时间，那么人眼将感知卡顿掉帧
/// 所以浏览器对一次重绘的渲染工作需要在 16ms（ 1000ms/60 ）之内完成，也就是说每一次重绘小于 16ms 才不会卡顿掉帧

/// requestIdleCallback 会在每一帧结束后判断浏览器是否空闲，如果浏览器一直处于占用状态，则没有空闲时间
/// 且如果 requestIdleCallback 没有设置 timeout 时间，那么 callback 的任务会一直推迟执行
/// 如果在当前帧设置 timeout，浏览器会在当前帧结束的下一帧开始判断是否超时执行 callback

// 浏览器出现空闲时段，调用 requestIdleCallback 的场景
// 1. 当浏览器一帧渲染所用时间小于屏幕刷新率（对于具有60Hz 的设备，一帧间隔应该小于16ms）时间，到下一帧渲染渲染开始时出现的空闲时间
// 2. 当浏览器没有可渲染的任务，主线程一直处于空闲状态，事件队列为空
//    为了避免在不可预测的任务（例如用户输入的处理）中引起用户可察觉的延迟，这些空闲周期的长度应限制为最大值 50ms = 1000 / 20
//    也就是 timeRemaining 最大不超过 50ms（也就是20fps，这也是 react polyfill 的原因之一）
//    当空闲时段结束时，可以调度另一个空闲时段，如果它保持空闲，那么空闲时段将更长，后台任务可以在更长时间段内发生

//    注意：timeRemaining 最大为 50毫秒 ，是根据研究[ https://w3c.github.io/requestidlecallback/#bib-responsetime ] 得出的
//    该研究表明，对用户输入的 100毫秒 以内的响应通常被认为对人类是瞬时的，就是人类不会有察觉
//    将闲置截止期限设置为 50ms 意味着即使在闲置任务开始后立即发生用户输入，用户代理仍然有剩余的 50ms 可以在其中响应用户输入而不会产生用户可察觉的滞后

// requestIdleCallback 注意事项：
// 1. requestIdleCallback 是屏幕渲染之后执行的
// 2. 一些低优先级的任务可使用 requestIdleCallback 等浏览器不忙的时候来执行
//    同时因为时间有限，它所执行的任务应该尽量是能够量化
//    细分的微任务（micro task）比较适合 requestIdleCallback
// 3. requestIdleCallback 不会和帧对齐，所以涉及到 DOM的操作 和 动画 最好放在 requestAnimationFrame 中执行
//    requestAnimationFrame 在重新渲染屏幕之前执行
// 4. Promise 也不建议在这里面进行，因为 Promise 的回调是在 Event loop 中优先级较高的一种微任务
//    会在 requestIdleCallback 结束时立即执行，不管此时是否还有富余的时间，这样有很大可能会让一帧超过 16 ms

/// requestIdleCallback polyfill
window.requestIdleCallback =
  window.requestIdleCallback ||
  function (cb: IdleRequestCallback, options?: IdleRequestOptions) {
    const start = now();
    // 退化为 setTimeout
    return setTimeout(() => {
      const diff = now() - start;
      cb({
        didTimeout: true, // 是否已到设置的超时时间
        timeRemaining: () => Math.max(0, 50 - diff) // 本帧剩余可执行时间，毫秒
      } as IdleDeadline);
    }, options?.timeout);
  };
/// cancelIdleCallback polyfill
window.cancelIdleCallback =
  window.cancelIdleCallback ||
  function (id: number) {
    clearTimeout(id);
  };

// 以最快20fps帧率，在浏览器空闲时，调用任务 - 几乎没用
export function scheduleIdle(
  cb: () => void,
  options?: {
    timeout?: number;
  }
) {
  window.requestIdleCallback(
    (deadline) => {
      if (deadline.timeRemaining() > 0 || deadline.didTimeout) {
        cb();
      } else {
        // cb() 未主动执行，循环调用 scheduleIdle
        scheduleIdle(cb, options);
      }
    },
    { timeout: options?.timeout }
  );
}

// https://www.npmjs.com/package/scheduler

/// React 任务优先级
// Immediate -1 需要立刻执行
// UserBlocking 250ms 超时时间250ms，一般指的是用户交互
// Normal 5000ms 超时时间5s，不需要直观立即变化的任务，比如网络请求
// Low 10000ms 超时时间10s，肯定要执行的任务，但是可以放在最后处理
// Idle 一些没有必要的任务，可能不会执行

/// 使用 rAF 来动态算出当前帧率:
/// 1. 假设初始帧率为 30fps，则每一帧执行时间为 1000 / 30 = 33ms
/// 2. 如果连续 2 帧的执行时间都小于当前帧率下的每帧执行时间，则表明当前帧率有待提高
/// 3. 如果连续 2 帧的执行时间都大于当前帧率下的每帧执行时间，则表明当前帧率有待减低

// 当前帧开始时间
let frameStartTimestamp = 0;
// 估算的当前帧截止时间
let frameEndTimestamp = 0;
// 估算的上一帧执行时间
let previousFrameDuration = 33;
// 估算的帧执行时间
let activeFrameDuration = 33;

// rAF 每一个 frame 中都会调用 currentCallback，currentCallback 自己根据 deadline.timeRemaining() 决定是否真正执行
let currentCallback: IdleRequestCallback;
let currentTimeout = -1;
const channel = new MessageChannel();
const port: MessagePort = channel.port2;
channel.port1.onmessage = (e: MessageEvent) => {
  if (currentCallback) {
    currentCallback({
      didTimeout:
        currentTimeout == -1
          ? false
          : now() - frameStartTimestamp > currentTimeout, // currentCallback 执行时，是否已到设置的超时时间
      timeRemaining: () => Math.max(0, frameEndTimestamp - now()) // 本帧剩余可执行时间，毫秒
    });
  }
};

// React v16.0.0 中实现 rIC 的核心思路就是，使用 rAF 动态计算帧率，使用 postMessage 实现异步调用
function requestIdleCallbackDynamic(
  cb: IdleRequestCallback,
  options?: {
    timeout?: number; // 超过这个时间后，如果任务还没执行，则强制执行，不必等待空闲
  }
): number {
  currentCallback = cb;
  currentTimeout = typeof options?.timeout == "number" ? options?.timeout : -1;
  // 如果直接使用 rAF 模拟 requestIdleCallback ，Fiber tree 的更新操作会在 rAF 里执行，整体增加了当前帧完成所需的时间，会导致帧率下降
  // 解决方案：使用 rAF 动态计算出当前帧率，得出帧截止时间，从而可以较准确的计算得到 rIC 中参数 timeRemaining
  return requestAnimationFrame((t: DOMHighResTimeStamp) => {
    frameStartTimestamp = t;
    // 估算的当前帧执行时间=估算的帧执行时间+上一帧实际执行时间与预期执行时间的差值，这里的 frameEndTimestamp 为上一帧的截止时间
    let currentFrameDuration =
      activeFrameDuration + frameStartTimestamp - frameEndTimestamp;
    if (
      currentFrameDuration < activeFrameDuration &&
      previousFrameDuration < activeFrameDuration
    ) {
      // 如果连续2帧的执行时间都小于帧执行时间，说明可以提高帧率
      // 最高 120 fps, 1000 / 120 = 8.3
      if (currentFrameDuration < 8) {
        currentFrameDuration = 8;
      }
      // 取连续2帧中执行时间较大的，防止执行超过帧截止时间
      activeFrameDuration =
        currentFrameDuration < previousFrameDuration
          ? previousFrameDuration
          : currentFrameDuration;
    } else if (
      currentFrameDuration > activeFrameDuration &&
      previousFrameDuration > activeFrameDuration
    ) {
      // 如果连续2帧的执行时间都大于帧执行时间，说明可以减低帧率
      // 取连续2帧中执行时间较大的，防止执行超过帧截止时间
      activeFrameDuration =
        currentFrameDuration < previousFrameDuration
          ? previousFrameDuration
          : currentFrameDuration;
    } else {
      // 帧率不变
      previousFrameDuration = currentFrameDuration;
    }
    frameEndTimestamp = frameStartTimestamp + activeFrameDuration;
    // 为了减少误差，使用 postMessage，而不是 setTimeout，因为 postMessage 比 setTimeout 要早得到执行
    // 异步调度越早开始，那么 timeRemaining 得到值的误差就会越小

    // 对于现代浏览器来说，使用 setTimeout(callback,0)，会存在 4ms 的执行间隔限制
    // https://html.spec.whatwg.org/multipage/timers-and-user-prompts.html#timers
    // If timeout is less than 0, then set timeout to 0.
    // If nesting level is greater than 5, and timeout is less than 4, then set timeout to 4.
    port.postMessage(null);
  });
}

const schedule = new ScheduleDependOnResult({
  concurrency: 1,
  timeRemaining: 0
});
let isCalled = false;
// 以当前帧率调用空闲任务
export function scheduleIdleAsap(
  cb: () => void,
  options?: {
    timeout?: number;
    name?: string;
  }
) {
  const task = createTask({
    fn: cb,
    name: options?.name,
    deadline:
      typeof options?.timeout == "number"
        ? milliseconds() + options.timeout
        : undefined
  });
  schedule.addTask(task);
  if (isCalled) {
    return;
  }
  isCalled = true;
  requestIdleCallbackDynamic((deadline) => {
    isCalled = false;
    schedule.idleCallback(deadline);
  });
}
